import { useState } from 'react';
import type { CommentSummary, NoteSummary } from '../../data/client';
import { useComments, useDeleteNote, useNotes, useUpdateNote } from '../../data/hooks';
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownSeparator,
  IconButton,
  Skeleton,
  Textarea,
  useToast,
} from '../../ui';
import { useSession } from '../auth/useSession';
import { AnnotationAuthor } from './AnnotationAuthor';
import { CommentEntryRow, type CommentTarget, CommentThread } from './CommentThread';

/**
 * NOTES, as group boxes with their thread beneath (notes-tasks-comments.md 12.1).
 *
 * A note is CONTENT, so it takes the content surface this screen has and no other: the
 * kit's group box wash on the window ground, never glass and never a card off the surface
 * ladder (wave 3, R-B1 - see the box below). The only glass on this screen is the control
 * layer: the segmented filter above and the capsule buttons.
 *
 * P5: THE COMPOSER HAS LEFT THIS PANEL. Writing a new note happens in the floating
 * writing panel (`NoteComposer`), because `spec/screens/record.md` section 1 puts
 * writing in a panel that stays above the window and never blocks it, and two ways to
 * start the same note is one way too many. EDITING an existing note keeps its inline
 * Save, because that is an edit of something already on the record and not a composer.
 */
export interface NotesPanelProps {
  readonly objectId: string;
  readonly recordId: string;
  /**
   * WAVE 4 (C2-12): a note's thread writes through the record's ONE writing panel, the same
   * one the stream's entry row opens, so this panel no longer carries a composer of its own.
   * Absent on an archived record, where the thread is read-only.
   */
  readonly onCompose?: ((target: CommentTarget) => void) | undefined;
}

export function NotesPanel({ objectId, recordId, onCompose }: NotesPanelProps) {
  const notes = useNotes(objectId, recordId);
  const comments = useComments(objectId, recordId);

  if (notes.isPending) {
    return (
      <div className="space-y-3" aria-busy="true">
        <Skeleton className="h-3 w-40" label="Loading the notes" />
        <Skeleton shape="block" className="h-24" />
      </div>
    );
  }

  const list = notes.data ?? [];
  /** Grouped client-side by `noteId`: one comments query per record serves both panels. */
  const threadsByNote = new Map<string, CommentSummary[]>();
  for (const comment of comments.data ?? []) {
    if (comment.noteId === null) continue;
    const bucket = threadsByNote.get(comment.noteId);
    if (bucket === undefined) threadsByNote.set(comment.noteId, [comment]);
    else bucket.push(comment);
  }

  return (
    <div className="space-y-4">
      {list.length === 0 ? (
        /* ONE LINE, not a block: the entry row above already offers the action, so this says
           what a note is for. Section 4's "Empty stream" wording, one filter down. */
        <p className="record-meta-line py-4">
          No notes. A note is where a call summary and the context around it live.
        </p>
      ) : (
        <ol className="space-y-3">
          {list.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              objectId={objectId}
              recordId={recordId}
              thread={threadsByNote.get(note.id) ?? []}
              {...(onCompose === undefined ? {} : { onCompose })}
            />
          ))}
        </ol>
      )}
    </div>
  );
}

interface NoteCardProps {
  readonly note: NoteSummary;
  readonly objectId: string;
  readonly recordId: string;
  readonly thread: readonly CommentSummary[];
  readonly onCompose?: ((target: CommentTarget) => void) | undefined;
}

function NoteCard({ note, objectId, recordId, thread, onCompose }: NoteCardProps) {
  const session = useSession();
  const updateNote = useUpdateNote(objectId, recordId);
  const deleteNote = useDeleteNote(objectId, recordId);
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);

  const mine =
    session.data?.role === 'admin' ||
    (session.data?.id !== undefined && note.createdBy.userId === session.data.id);

  const save = (): void => {
    const trimmed = draft.trim();
    if (trimmed === '') return;
    updateNote.mutate(
      {
        noteId: note.id,
        body: trimmed,
        /** The row's `updated_at` as last read: a mid-air collision answers 409. */
        ifUnmodifiedSince: note.updatedAt,
      },
      {
        onSuccess: () => setEditing(false),
        onError: (error) =>
          toast({
            title: 'This note changed while you were editing',
            description: error.message,
            tone: 'danger',
          }),
      },
    );
  };

  return (
    /*
      WAVE 3 (R-B1): THE NOTE SITS ON THE GROUP BOX WASH, like everything else on this screen.
      It was `bg-surface` with a hairline border - the surface ladder's card, which belongs on
      a canvas - and against the record's window ground that reads as a black sheet in dark
      and as white on white in light (owner-34, owner-36). `.record-group` is the one content
      surface this screen has, and it already switches from the macOS wash to the iOS card at
      the breakpoint, so a note follows the platform without a rule of its own.
    */
    <li className="record-group record-note">
      <div className="flex items-start justify-between gap-3">
        <AnnotationAuthor
          createdBy={note.createdBy}
          createdByName={note.createdByName}
          updatedByName={note.updatedByName}
          at={note.createdAt}
          humanTouchedAt={note.humanTouchedAt}
          edited={note.updatedAt !== note.createdAt}
        />
        {mine && (
          <DropdownMenu
            trigger={<IconButton icon="more-horizontal" label="Note actions" />}
            align="end"
          >
            <DropdownItem
              icon="pencil"
              onSelect={() => {
                setDraft(note.body);
                setEditing(true);
              }}
            >
              Edit
            </DropdownItem>
            <DropdownSeparator />
            <DropdownItem
              icon="trash"
              tone="danger"
              onSelect={() =>
                deleteNote.mutate(note.id, {
                  onSuccess: () => toast({ title: 'Note deleted', tone: 'neutral' }),
                })
              }
            >
              Delete
            </DropdownItem>
          </DropdownMenu>
        )}
      </div>

      {editing ? (
        <div className="mt-3 space-y-2">
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            aria-label="Edit the note"
            rows={4}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            {/* SECONDARY, not the accent-filled primary (R-B1). A screen has one prominent
                action and on the record that is the toolbar's; an accent-filled Save inside a
                note put a second one in the stream, which is the competition DESIGN.md's
                "one hierarchy per screen" names. The writing panel's own verb is secondary
                for the same reason. */}
            <Button variant="secondary" onClick={save} loading={updateNote.isPending}>
              Save
            </Button>
          </div>
        </div>
      ) : (
        /*
          `whitespace-pre-wrap`: the server stores and diffs PLAIN TEXT, and the one thing
          a reader needs back is the shape they typed - paragraph breaks in a call summary
          are the summary's structure.
        */
        <p className="mt-2 text-sm whitespace-pre-wrap text-ink">{note.body}</p>
      )}

      {(thread.length > 0 || onCompose !== undefined) && (
        <div className="mt-3 border-t border-line-subtle pt-3">
          <CommentThread
            comments={thread}
            objectId={objectId}
            recordId={recordId}
            noteId={note.id}
            onCompose={onCompose ?? (() => undefined)}
          />
          {onCompose !== undefined && (
            <CommentEntryRow
              label="Comment on this note"
              onClick={() => onCompose({ noteId: note.id })}
            />
          )}
        </div>
      )}
    </li>
  );
}
